<template>
  <div id="batch-history" v-if="isSignedIn">
    <div id="batch-title">
      <a-space>
        <h1>Batch History</h1>
        <a-button @click="toggleModal" type="primary">Run Batch</a-button>
        <a-button @click="fetchLogs()" :loading="isLoading">Refresh</a-button>
        <a-divider type="vertical" />
        View
        <a-select default-value="" style="width: 120px" @change="handleTargetFilterChange">
          <a-select-option value="">
            All
          </a-select-option>
          <a-select-option value="Production">
            Production
          </a-select-option>
          <a-select-option value="Test">
            Sandbox
          </a-select-option>
        </a-select>
      </a-space>
    </div>

    <a-table
            :columns="columns"
            :data-source="displayData"
            class="components-table-demo-nested"
            :pagination="false"
            :rowKey="record => record.id"
            :loading="isLoading"
            :rowClassName="() => 'clickable'"
            :customRow="customRowProps"
    >
      <div class="center-col" slot="view_icon" slot-scope="value, record, index">
        <a-icon v-if="iconVisibleIndex === index" type="search" :style="{fontSize: '18px', color:'gray'}" />
      </div>
      <div class="center-col" slot="id" slot-scope="record">{{record}}</div>
      <div class="center-col" slot="batch_type" slot-scope="record">{{record}}</div>
      <div class="center-col" slot="import_regular_status" slot-scope="record">{{record}}</div>
      <div class="center-col" slot="created_at" slot-scope="record">{{record? formatDate(record) : ''}}</div>
      <div class="center-col" slot="import_destination" slot-scope="record">
        <a-tag
                :key="record"
                :color="record == 'Production' ? '#f90' : '#66cd00'">
          {{formatTarget(record)}}
        </a-tag>
      </div>

      <div class="center-col" slot="time_off_success_count" slot-scope="record">{{record}}</div>
      <div class="center-col" slot="time_off_error_count" slot-scope="record">{{record}}</div>
      <div class="center-col" slot="time_off_unacknowledged_error_count" slot-scope="record">
        <a-tag
                :key="record"
                :color="record > 0 ? 'red' : 'blue'">
          {{record}}
        </a-tag>
      </div>
      <div class="center-col" slot="worked_time_success_count" slot-scope="record">{{record}}</div>
      <div class="center-col" slot="worked_time_error_count" slot-scope="record">{{record}}</div>
      <div class="center-col" slot="worked_time_unacknowledged_error_count" slot-scope="record">
        <a-tag
                :key="record"
                :color="record > 0 ? 'red' : 'blue'">
          {{record}}
        </a-tag>
      </div>

      <div class="center-col" slot="start_date" slot-scope="record">{{record}}</div>
      <div class="center-col" slot="end_date" slot-scope="record">{{record}}</div>
    </a-table>

    <a-modal
            title="Run Batch"
            :visible="modalVisible"
            :confirm-loading="modalLoading"
            @cancel="toggleModal"
            width="50vw"
    >
      <template slot="footer">
        <a-button key="back" @click="toggleModal">{{modalResultVisible ? "OK" : "Cancel"}}</a-button>
      </template>
      <div class="modal-body">
        <template v-if="!modalResultVisible">
          This allows you to manually kick off a batch. You may select a specific start date, end date and destination, the process will
          import to the selected Workday instance all time entries from that date range.
          <p class="modal-content">
            Start Date:
            <a-date-picker
                    class="selection"
                    :format="dateFormat"
                    :disabledDate="disabledDate"
                    @change="setStartDate"
            />
            End Date:
            <a-date-picker
                    class="selection"
                    :format="dateFormat"
                    :disabledDate="disabledDate"
                    @change="setEndDate"
            />
            Send Batch To:
            <a-select :defaultActiveFirstOption="false"
                      :value="importTarget"
                      :allowClear="true"
                      placeholder="Send To"
                      style="width: 120px"
                      @change="handleImportTargetChange" >
              <a-select-option value="Production">
                Production
              </a-select-option>
              <a-select-option value="Test">
                Sandbox
              </a-select-option>
            </a-select>

            <a-button @click="runBatch()" type="primary"
                      :disabled="(startDate == null || endDate == null) || (endDate < startDate) || !importTarget"
                      :loading="runBatchLoading">
              {{runBatchLoading ? "Adding.." : "Run Batch"}}
            </a-button>
          </p>
          <p
                  v-if="((!!startDate && !!endDate) && (endDate >= startDate))"
          >Time entries from {{formatMMMMDDYYYY(startDate)}} to {{formatMMMMDDYYYY(endDate)}} will be imported.</p>
        </template>
        <template v-else-if="runBatchSuccessful">
          <a-result
                  status="success"
                  title="Batch Successfully Submitted For Processing!"
                  sub-title="The batch will take approximately 15 minutes to complete. Pulling data from Mavenlink, preparing and submitting it to Workday happens fairly fast."
          >
            <div class="result-container">
              <p>
                <a-icon type="info-circle" /> For PTO records, the results from Workday will be immediately available after processing.
              </p>
              <p>
                <a-icon type="info-circle" /> For worked time records, Workday needs time to process them properly. To make sure that Workday has finished the processing, the batch process waits 15 minutes before querying Workday for results on the records.
              </p>
            </div>
            <a-divider />
            To monitor the status of the batch, you can click on the Refresh button in the Batch History page.
          </a-result>
        </template>
        <template v-else>
          <a-result
                  status="error"
                  title="Failed To Process Batch"
                  sub-title="Encountered an issue processing the batch."
          >
            <div class="result-container">
              <p class="center" id=result-header>
                <strong>Some possible issues to check before re-submitting:</strong>
              </p>
              <p class="center">
                <a-icon :style="{ color: 'red' }" type="exclamation-circle" /> Intermittent network connection
              </p>
              <p class="center">
                <a-icon :style="{ color: 'red' }" type="exclamation-circle" /> Invalid date range parameters
              </p>
              <p class="center">
                <a-icon :style="{ color: 'red' }" type="exclamation-circle" /> Batch queueing service is down
              </p>
            </div>
          </a-result>
        </template>

      </div>
    </a-modal>
  </div>
</template>

<script>
  import moment from "moment";
  import { mapState, mapGetters, mapMutations } from "vuex";
  import axios from "axios";

  const columns = [
    {
      key: "view_icon",
      width: 55,
      scopedSlots: { customRender: "view_icon" }
    },
    {
      title: "Start Date",
      dataIndex: "start_date",
      key: "start_date",
      width: 120,
      scopedSlots: { customRender: "start_date" }
    },
    {
      title: "End Date",
      dataIndex: "end_date",
      key: "end_date",
      width: 120,
      scopedSlots: { customRender: "end_date" }
    },
    {
      title: "Import Status",
      dataIndex: "import_regular_status",
      key: "import_regular_status",
      scopedSlots: { customRender: "import_regular_status" }
    },

    // {
    //   title: "Time Off Count",
    //   dataIndex: "time_off_count",
    //   key: "time_off_count",
    //   scopedSlots: { customRender: "time_off_count" }
    // },
    {
      title: "Time Off Success Count",
      dataIndex: "time_off_success_count",
      key: "time_off_success_count",
      scopedSlots: { customRender: "time_off_success_count" }
    },
    {
      title: "Time Off Error Count",
      dataIndex: "time_off_error_count",
      key: "time_off_error_count",
      scopedSlots: { customRender: "time_off_error_count" }
    },
    {
      title: "Time Off Unacknowledged Error Count",
      dataIndex: "time_off_unacknowledged_error_count",
      key: "time_off_unacknowledged_error_count",
      scopedSlots: { customRender: "time_off_unacknowledged_error_count" }
    },
    // {
    //   title: "Worked Time Count",
    //   dataIndex: "worked_time_count",
    //   key: "worked_time_count",
    //   scopedSlots: { customRender: "worked_time_count" }
    // },
    {
      title: "Worked Time Success Count",
      dataIndex: "worked_time_success_count",
      key: "worked_time_success_count",
      scopedSlots: { customRender: "worked_time_success_count" }
    },
    {
      title: "Worked Time Error Count",
      dataIndex: "worked_time_error_count",
      key: "worked_time_error_count",
      scopedSlots: { customRender: "worked_time_error_count" }
    },
    {
      title: "Worked Time Unacknowledged Error Count",
      dataIndex: "worked_time_unacknowledged_error_count",
      key: "worked_time_unacknowledged_error_count",
      scopedSlots: { customRender: "worked_time_unacknowledged_error_count" }
    },
    {
      title: "Batch ID", dataIndex: "id", key: "id", scopedSlots: { customRender: "id" }
    },
    {
      title: "Batch Type",
      dataIndex: "batch_type",
      key: "batch_type",
      scopedSlots: { customRender: "batch_type" }
    },
    {
      title: "Target",
      dataIndex: "import_destination",
      key: "import_destination",
      width: 90,
      scopedSlots: { customRender: "import_destination" }
    },
    {
      title: "Started At",
      dataIndex: "created_at",
      key: "created_at",
      scopedSlots: { customRender: "created_at" }
    },
  ];

  export default {
    name: "BatchHistory",
    computed: {
      ...mapState(["apiUrl"]),
      ...mapGetters('auth', ["isSignedIn","getUUID"]),

    },
    data() {
      return {
        columns,
        data: [],
        displayData: [],
        importTarget: null,
        targetFilter: null,
        isLoading: false,
        iconVisibleIndex: null,
        modalVisible: false,
        modalLoading: false,
        modalResultVisible: false,
        dateFormat: "YYYY-MM-DD",
        startDate: null,
        endDate: null,
        runBatchSuccessful: true,
        runBatchLoading: false,
      };
    },
    methods: {
      ...mapMutations('auth', ["signInFailed"]),
      fetchLogs() {
        this.isLoading = true;
        axios({
          url: `${this.apiUrl}/getBatchEntries`,
          params: {
            action: "FETCH_BATCHES",
          },
          headers: {
            Authorization: 'Bearer ' + this.getUUID
          }
        })
          .then(rslt => {
            this.data = rslt.data;
            this.updateDisplayData();
            this.isLoading = false;
          })
          .catch(err => {
            console.log(`[BatchHistory] ${err}`);

            //In the case the endpoint returns a 401, that means
            // our session token has expired. Reroute to /sso-login
            if(err.response.status == 401) {
              this.signInFailed("Session has expired.");
              this.$router.push(`/sso-login`);
            }
            else {
              alert("Unable to load logs");
              this.isLoading = false;
            }
          });
      },
      toggleModal() {
        this.modalVisible = !this.modalVisible;
        // reset modal when closing it
        if (!this.modalVisible) {
          // Refresh table if coming from the Run Batch result modal
          if (this.modalResultVisible) {
            this.fetchLogs();
          }
          this.resetModal();
        }
      },
      disabledDate(current) {
        return current && current > moment().endOf("day");
      },
      setStartDate(date) {
        let d = new Date(date);
        d.setHours(0, 0, 0, 0);
        this.startDate = d;
      },
      setEndDate(date) {
        let d = new Date(date);
        d.setHours(0, 0, 0, 0);
        this.endDate = d;
      },
      formatMMMMDDYYYY(date) {
        return moment(date).format("MMMM DD, YYYY");
      },
      formatDate(d) {
        const date = new Date(d);
        return date.toLocaleString();
      },
      formatTarget(importDest) {
        // Replace 'Test' with 'Sandbox'
        return (importDest == "Test" ? "Sandbox" : importDest).substring(0, 1);
      },
      runBatch() {
        this.runBatchLoading = true;

        axios({
          method: "post",
          url: `${this.apiUrl}/runBatchManual`,
          params: {
            endDate: moment(this.endDate).format("YYYY-MM-DD"),
            numOfDays: moment.duration(moment(this.endDate).diff(moment(this.startDate))).asDays() + 1, //+1 since date range in computation is inclusive!
            target: this.importTarget,
          },
          headers: {
            Authorization: 'Bearer ' + this.getUUID
          }
        })
          .then(response => {
            this.displayModalResult(true);
          })
          .catch(err => {
            console.log(err);

            //In the case the endpoint returns a 401, that means
            // our session token has expired. Reroute to /sso-login
            if(err.response.status == 401) {
              this.signInFailed("Session has expired.");
              this.$router.push(`/sso-login`);
            }
            else {
              this.displayModalResult(false);
            }
          });

        this.fetchLogs();

      },
      displayModalResult(isSuccess) {
        this.runBatchSuccessful = isSuccess;
        this.modalResultVisible = true;
      },
      resetModal() {
        this.runBatchLoading = false;
        this.startDate = null;
        this.endDate = null;
        this.importTarget = null;
        this.modalResultVisible = false;
      },
      signOutButton() { // TODO apply better naming for the method
        console.log("Sign out!")
        this.signOut(); // the page will be redirected to the postLogoutRedirectUri of msalConfig
      },
      customRowProps(record, index) {
        return {
          props: {
          },
          on: {
            // click row
            click: (event) => {
              this.$router.push(`/batch/${record.id}`);
            },
            mouseenter: (event) => {
              this.iconVisibleIndex = index;
            },
            mouseleave: (event) => {
              this.iconVisibleIndex = null;
            },
          },
        }
      },
      handleTargetFilterChange(value) {
        this.targetFilter = value;
        this.updateDisplayData();
      },
      updateDisplayData() {
        this.displayData = this.targetFilter ? this.data.filter((t) => t.import_destination==this.targetFilter) : this.displayData = this.data;
      },
      handleImportTargetChange(value) {
        this.importTarget = value;
      },
    },
    mounted() {
      this.$message.config({
        maxCount: 1
      })
      this.isLoading = true;
      this.fetchLogs();
    },
  }
</script>

<style lang="scss" scoped>
  #batch-title {
    display: flex;
    align-items: center;
    margin-bottom: 10px;

    h1 {
      margin: 0 10px 0 0;
    }
  }

  .modal-body {
    ::v-deep {
      .ant-result {
        padding: 24px 16px;
      }
    }

    .modal-content {
      display: flex;
      margin: 20px;
      justify-content: space-evenly;

      .selection {
        width: 130px;
      }
    }
  }

  .result-container {
    display: flex;
    flex-direction: column;
    justify-content: center;

    .center {
      text-align: center;
    }
  }

  #result-header {
    font-size: 16px;
  }

  ::v-deep {
    .ant-table-thead > tr > th,
    .center-col {
      text-align: center;

      &[key="PageName"] {
        text-align: left;
      }
    }

    .clickable {
      cursor: pointer;
    }
  }
</style>